import React from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useParams } from "react-router";
import { useForm, Controller } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import loader from "@/images/loader.gif";
import PlacePicker from "@/components/PlacePicker";
import Field from "@/components/FormInput/Field";
import { normalizeLocationMap } from "@/utils/location";
import { createHub } from "@/store/hubs/hubActions";
import {
  buttonRowSx,
  formSx,
  pageIndexSx,
  requiredPromptSx,
  titleSx,
} from "./styles";

interface OrganizationType {
  id: number;
  label: string;
}

interface StepOneValues {
  location_map: unknown;
  organization_type_id: string;
  website: string;
}

const CreateHubStepOne = () => {
  const { t } = useTranslation(["translation.Hub"]);
  const dispatch = useDispatch();
  useParams(); // kept for route stability; h_id no longer needed by createHub

  const { organizationTypes, hubName, isRequesting } = useSelector(
    (state: any) => {
      const { appData, metadata, invitations } = state;
      const processingHubInvitation = invitations.createHubInvitations.find(
        (chi: any) => chi.get("token") === invitations.createHubInvitationToken
      );

      return {
        organizationTypes: appData.organizationTypes
          .sortBy((ot: any) => ot.get("label"))
          .toJS() as OrganizationType[],
        hubName: processingHubInvitation.get("hub_name") as string,
        isRequesting: Boolean(metadata.isRequesting),
      };
    },
    shallowEqual
  );

  const { register, handleSubmit, control } = useForm<StepOneValues>({
    defaultValues: {
      location_map: null,
      organization_type_id: "",
      website: "",
    },
  });

  const onSubmit = (values: StepOneValues) => dispatch(createHub(values));

  // Field forwards `inputRef` to its TextField, so register()'s ref goes
  // there and the rest of register()'s props spread onto the field.
  const { ref: websiteRef, ...websiteField } = register("website");

  return (
    <Dialog
      open
      // Esc goes back a step; a backdrop click does not, so a stray click
      // outside the form cannot discard a half-filled hub.
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") window.history.back();
      }}
      maxWidth={false}
      slotProps={{ paper: { "aria-label": t("CreateHubModal") } }}
    >
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={formSx}>
          <Typography variant="h2" sx={titleSx}>
            {t("CreateYourHub")} - {hubName}
          </Typography>
          <Box sx={requiredPromptSx}>{t("*required")}</Box>

          <Stack spacing={3}>
            <Controller
              name="location_map"
              control={control}
              render={({ field }) => (
                <PlacePicker
                  value={normalizeLocationMap(field.value)}
                  onChange={field.onChange}
                  label={`${t("Location")}*`}
                  placeholder={t("LocationPlaceholder")}
                />
              )}
            />

            <Controller
              name="organization_type_id"
              control={control}
              render={({ field }) => (
                <Field select label={`${t("Type")}*`} {...field}>
                  <MenuItem disabled value="">
                    {t("SelectType")}
                  </MenuItem>
                  {organizationTypes.map((ot) => (
                    <MenuItem key={ot.id} value={ot.id}>
                      {ot.label}
                    </MenuItem>
                  ))}
                </Field>
              )}
            />

            <Field
              type="text"
              label={t("Website")}
              placeholder="https://www.hub-name.com"
              {...websiteField}
              inputRef={websiteRef}
            />
          </Stack>

          <Stack direction="row" sx={buttonRowSx}>
            <Box component="span" sx={pageIndexSx}>
              1/2
            </Box>
            {isRequesting ? (
              <Button disabled startIcon={<img src={loader} alt="" />}>
                {t("Working")}
              </Button>
            ) : (
              <Button type="submit" variant="contained">
                {t("Next")} &gt;
              </Button>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateHubStepOne;
